import { isEqual } from 'lodash';
import { useRef } from 'react';
import { Form, Formik } from 'formik';

import { checkIfIsArray, invariant } from '@e-socna/utils';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import { useUiContext } from '../../contexts/UiContext/hooks/useUiContext.hook';
import { MemoBeforeSubmitForm } from '../../forms/MemoBeforeSubmit';
import { memoAwareSubmitHandler } from '../../forms/MemoBeforeSubmit/utils/memo-aware-submit-handler.util';
import { FormVariant } from '../../forms';

import { ConfirmBeforeExitForm } from '../ConfirmBeforeExitForm';
import { FORM_LAYOUT_STYLE, FormLayout } from '../FormLayout';
import { AlertMessages } from '../AlertMessages';

import { useConsolidation } from './hook/useConsolidation.hook';
import { Stepper } from './Stepper';
import { useSteps } from './hook/useSteps.hook';
import { useActions } from './hook/useActions.hook';
import { useEnterPressesSubmit } from './hook/useEnterPressesSubmit.hook';
import { useWizardContext } from './contexts/WizardContext/hooks/useWizardContext.hook';

// MHO : https://github.com/jaredpalmer/formik/blob/master/examples/MultistepWizard.js
// Wizard is a single Formik instance whose children are each page of the
// multi-step form. The form is submitted on each forward transition (can only
// progress with valid input), whereas a backwards step is allowed with
// incomplete data. A snapshot of form state is used as initialValues after each
// transition. Each page has an optional submit handler, and the top-level
// submit is called when the final page is submitted.
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export const WizardForm = <V extends object, VS extends object, SE>() => {
  const {
    clearMemos,
    isSubmitLocked,
    memos,
    props: properties,
    setCurrentStepId,
    setMemos,
    snapshot,
    wizardError,
  } = useWizardContext<V, VS, SE>();

  const {
    askMemoBeforeSubmit,
    formTitle,
    formVariant,
    onCancel: handleCancel,
    setMustBlockAction,
    showCancelButton,
    steps,
    wizardChildren,
  } = properties;

  const {
    consolidatedInitialValues,
    consolidatedValidate,
    ConsolidatedValidationSchema,
  } = useConsolidation();

  const formRef = useRef<HTMLFormElement | null>(null);
  useEnterPressesSubmit(formRef);

  invariant(
    checkIfIsArray(wizardChildren) && !!wizardChildren.length,
    'Wizard must have at least one children',
  );

  invariant(
    !wizardChildren.some(
      (child) => typeof child === 'string' || typeof child === 'number',
    ),
    'Wizard children must be React elements',
  );

  invariant(
    wizardChildren.length === steps.length,
    'Wizard children and steps must have the same length',
  );

  const { ui } = useUiContext();

  const {
    currentEnabledIndexInEnabledSteps,
    isLastEnabledStep,
    currentEnabledIndexInAllSteps,
    currentEnabledStepCmp,
    childrenSteps,
  } = useSteps<V, VS, SE>();

  const { handleSubmit, previous, showMemoDialog } = useActions<V, VS, SE>({
    formRef,
  });

  return (
    <Formik
      enableReinitialize
      initialValues={snapshot}
      onSubmit={handleSubmit}
      validate={consolidatedValidate}
      // validateOnMount
      validationSchema={ConsolidatedValidationSchema}
    >
      {(formikBag) => {
        const {
          handleSubmit: formikSubmit,
          isSubmitting,
          isValid,
          submitForm,
          values,
        } = formikBag;
        const isLoading = isSubmitting || ui.isBackgroundOperationRunning;

        // MHO : we consider in a wizard that the form is dirty when the user has reached a step that is not the first one
        const customDirty =
          !isEqual(values, consolidatedInitialValues) ||
          currentEnabledIndexInEnabledSteps;

        const mustBlock = !!customDirty && !(isSubmitting && isLastEnabledStep);

        const handleFormSubmit = memoAwareSubmitHandler({
          askMemo: showMemoDialog,
          mustAskMemo: !!(
            isLastEnabledStep &&
            askMemoBeforeSubmit &&
            customDirty &&
            isValid
          ),
          submit: formikSubmit,
        });

        const submitLabel = !isLastEnabledStep
          ? 'Suivant'
          : formVariant === FormVariant.Creation
            ? 'Créer'
            : 'Modifier';

        return (
          <Form
            onSubmit={handleFormSubmit}
            ref={formRef}
            // eslint-disable-next-line react/forbid-component-props
            style={FORM_LAYOUT_STYLE}
          >
            <ConfirmBeforeExitForm
              mustBlock={mustBlock}
              setMustBlockAction={setMustBlockAction}
            />
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
            {!!askMemoBeforeSubmit && (
              <MemoBeforeSubmitForm
                clearMemos={clearMemos}
                isLoading={isLoading}
                memos={memos}
                onConfirm={submitForm}
                setMemos={setMemos}
                submitLabel={submitLabel}
              />
            )}
            <FormLayout
              actions={
                <Stack direction="row" spacing={2}>
                  <Button
                    // MHO : had to disable this whole rule since sometimes steps do not include mandatory fields.
                    // example : In material form, second step when we can choose costs.
                    // If user doesn't want, he can't submit ...
                    // So for now, I only kept a check on validity.
                    // disabled={isLoading || !dirty || !isValid}
                    disabled={!isValid || isSubmitLocked}
                    loading={isLoading}
                    size="large"
                    type="submit"
                    variant="contained"
                  >
                    {submitLabel}
                  </Button>
                  {(!!showCancelButton ||
                    !!currentEnabledIndexInEnabledSteps) && (
                    <Button
                      disabled={isLoading}
                      // eslint-disable-next-line react/jsx-no-bind
                      onClick={
                        currentEnabledIndexInEnabledSteps
                          ? () => {
                              previous(values, formikBag);
                            }
                          : handleCancel
                      }
                      size="large"
                      variant="outlined"
                    >
                      {currentEnabledIndexInEnabledSteps ? 'Retour' : 'Annuler'}
                    </Button>
                  )}
                </Stack>
              }
              error={
                !!wizardError && (
                  <AlertMessages
                    messages={wizardError.messages}
                    severity="error"
                  />
                )
              }
              header={
                childrenSteps.length > 1 && (
                  <Box sx={{ mb: 4 }}>
                    <Stepper
                      activeStep={currentEnabledIndexInAllSteps}
                      setCurrentStepId={setCurrentStepId}
                      steps={steps}
                    />
                  </Box>
                )
              }
              title={formTitle}
            >
              {currentEnabledStepCmp}
            </FormLayout>
          </Form>
        );
      }}
    </Formik>
  );
};
